import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';
import usePrivacyMaskValue from 'app-components/hooks/privacyMode/usePrivacyMaskValue';
import { buildBarChartOption } from 'utils/chart-options';
import type { BarChartConfig, ChartDataItem } from 'utils/chart-options';
import React, { useMemo, useRef, useState, useCallback, useImperativeHandle, forwardRef, type JSX } from 'react';
import type { EChartsOption } from 'echarts';

import EChartsBase, { type ChartCoordinateApi, type EChartsBaseHandle } from '../EChartsBase';
import ChartWindowControls from '../ChartWindowControls';
import { useAutoRotateCategoryAxisLabels } from '../useAutoRotateCategoryAxisLabels';
import useEChartsTheme from '../useEChartsTheme';

const MAX_VISIBLE_BARS = 31;

// Wrappers that overlay HTML anchored to data coordinates (RangeBarChart) read coordinates
// through the readiness-safe ChartCoordinateApi, which is forwarded verbatim from EChartsBase.
// The raw instance is deliberately not exposed — see utils/chart-options/CLAUDE.md.
export type BarChartRef = ChartCoordinateApi & {
  clearSelection: () => void;
  setSelection: (index: number) => void;
};

export interface BarChartProps {
  orderedFields?: string[];
  data?: ChartDataItem[];
  stacked?: boolean;
  height?: number | string;
  onBarSelect?: (index?: number) => void;
  withLegend?: boolean;
  monthlyAverage?: number;
  alwaysShowTargetLine?: boolean;
  tooltipTotalLabel?: string;
  absoluteValues?: boolean;
  animated?: boolean;
  colorOverrides?: string[];
  isInDrillDownView?: boolean;
  customDateText?: boolean;
  // Pass to drive selection from outside without a ref+effect (avoids double-render on mount)
  selectedIndex?: number | null;
  renderer?: 'svg' | 'canvas';
  // Object form shallow-merges over the built option; function form receives the
  // fully built option and returns a transformed one (for deep edits such as
  // adding markLine/markArea to the series).
  optionOverrides?: Record<string, unknown> | ((option: EChartsOption) => EChartsOption);
  showZoomSlider?: boolean;
  xAxisTitle?: string;
  yAxisTitle?: string;
  title?: string;
  showGridLines?: boolean;
  simpleTooltip?: boolean;
  showTooltipCents?: boolean;
}

const BarChart = React.memo(
  forwardRef<BarChartRef, BarChartProps>(function BarChart(
    {
      data,
      orderedFields,
      stacked,
      height = 400,
      onBarSelect,
      withLegend = false,
      monthlyAverage,
      alwaysShowTargetLine,
      tooltipTotalLabel,
      absoluteValues,
      animated = true,
      colorOverrides,
      isInDrillDownView = false,
      customDateText = false,
      selectedIndex: controlledSelectedIndex,
      renderer = 'canvas',
      optionOverrides,
      showZoomSlider = false,
      xAxisTitle,
      yAxisTitle,
      title,
      showGridLines,
      simpleTooltip,
      showTooltipCents,
    }: BarChartProps,
    ref,
  ): JSX.Element | null {
    const theme = useEChartsTheme();
    const formatAmount = useFormatAmount();
    const privacyMask = usePrivacyMaskValue();
    const chartRef = useRef<EChartsBaseHandle>(null);

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    // Mutated directly (not React state) so mousemove-driven hover doesn't re-render the
    // component or recompute `option` on every pixel of cursor movement across bars. The
    // tooltip formatter reads .current fresh each time ECharts invokes it.
    const hoveredFieldNameRef = useRef<string | null>(null);

    // controlledSelectedIndex takes precedence when provided (avoids ref+effect double-render).
    // Falls back to internal click-driven state for the BarChartWrapper drilldown path.
    const resolvedIndex = controlledSelectedIndex !== undefined ? controlledSelectedIndex : selectedIndex;
    const effectiveSelectedIndex = isInDrillDownView ? resolvedIndex : null;

    useImperativeHandle(ref, () => ({
      clearSelection: () => setSelectedIndex(null),
      setSelection: (index: number) => setSelectedIndex(index),
      // Forwarded one-for-one. The fallbacks cover the window before EChartsBase's own handle is
      // attached, where chartRef.current is still null.
      isReady: () => chartRef.current?.isReady() ?? false,
      convertToPixel: (finder, value) => chartRef.current?.convertToPixel(finder, value) ?? null,
      convertFromPixel: (finder, pixel) => chartRef.current?.convertFromPixel(finder, pixel) ?? null,
      containPixel: (finder, pixel) => chartRef.current?.containPixel(finder, pixel) ?? null,
      getSize: () => chartRef.current?.getSize() ?? null,
      subscribeReady: (listener) => chartRef.current?.subscribeReady(listener) ?? (() => {}),
    }));

    const totalCount = data?.length ?? 0;
    const needsWindowing = totalCount > MAX_VISIBLE_BARS;
    const categoryLabels = useMemo(() => data?.map((item) => item.category) ?? [], [data]);
    const handleChartReady = useAutoRotateCategoryAxisLabels({ labels: categoryLabels, fontFamily: theme.fontFamily });

    const initialStart = Math.max(0, totalCount - MAX_VISIBLE_BARS);

    const [{ start: storedStart, forCount }, setWindow] = useState({ start: initialStart, forCount: totalCount });
    const windowStart = forCount === totalCount ? storedStart : initialStart;
    const windowEnd = Math.min(windowStart + MAX_VISIBLE_BARS - 1, totalCount - 1);

    const moveWindow = useCallback(
      (newStart: number) => {
        const clampedStart = Math.max(0, Math.min(newStart, totalCount - MAX_VISIBLE_BARS));
        setWindow({ start: clampedStart, forCount: totalCount });
        // Safe to drop if the chart is mid-(re)initialization: dataZoom.startValue/endValue are
        // rebuilt from windowStart/windowEnd below, so the next render re-applies the window.
        chartRef.current?.dispatchAction({
          type: 'dataZoom',
          dataZoomIndex: 0,
          startValue: clampedStart,
          endValue: Math.min(clampedStart + MAX_VISIBLE_BARS - 1, totalCount - 1),
        });
      },
      [totalCount],
    );

    const onPrev = useCallback(() => moveWindow(windowStart - MAX_VISIBLE_BARS), [moveWindow, windowStart]);
    const onNext = useCallback(() => moveWindow(windowStart + MAX_VISIBLE_BARS), [moveWindow, windowStart]);

    const option = useMemo(() => {
      if (!data?.length || !orderedFields?.length) return null;
      const config: BarChartConfig = {
        theme,
        animated,
        formatAmount,
        privacyMask,
        stacked,
        withLegend,
        selectedIndex: effectiveSelectedIndex,
        monthlyAverage,
        alwaysShowTargetLine,
        tooltipTotalLabel,
        absoluteValues,
        height,
        colorOverrides,
        customDateText,
        xAxisTitle,
        yAxisTitle,
        title,
        showGridLines,
        simpleTooltip,
        showTooltipCents,
        hoveredFieldNameRef,
      };
      const raw = buildBarChartOption(data, orderedFields, config);
      if (!needsWindowing && !showZoomSlider) return raw;

      const dataZoom = [
        {
          type: 'inside',
          xAxisIndex: 0,
          startValue: windowStart,
          endValue: windowEnd,
          ...(showZoomSlider ? {} : { maxValueSpan: MAX_VISIBLE_BARS - 1 }),
          zoomOnMouseWheel: false,
          moveOnMouseWheel: false,
          preventDefaultMouseMove: true,
        },
        ...(showZoomSlider
          ? [
              {
                type: 'slider',
                xAxisIndex: 0,
                startValue: windowStart,
                endValue: windowEnd,
                height: 20,
                bottom: 4,
                brushSelect: false,
              },
            ]
          : []),
      ];

      return {
        ...raw,
        ...(showZoomSlider ? { grid: { ...(raw as any).grid, bottom: 52 } } : {}),
        dataZoom,
      };
    }, [
      data,
      orderedFields,
      theme,
      animated,
      formatAmount,
      privacyMask,
      stacked,
      withLegend,
      effectiveSelectedIndex,
      monthlyAverage,
      alwaysShowTargetLine,
      tooltipTotalLabel,
      absoluteValues,
      height,
      colorOverrides,
      customDateText,
      needsWindowing,
      showZoomSlider,
      windowStart,
      windowEnd,
      xAxisTitle,
      yAxisTitle,
      title,
      showGridLines,
      simpleTooltip,
      showTooltipCents,
    ]);

    const onEvents = useMemo(
      () => ({
        click: (params: { componentType?: string; dataIndex?: number }) => {
          if (!onBarSelect || params.componentType !== 'series' || typeof params.dataIndex !== 'number') return;
          const isDeselect = typeof effectiveSelectedIndex === 'number' && params.dataIndex === effectiveSelectedIndex;
          setSelectedIndex(isDeselect ? null : params.dataIndex);
          onBarSelect(isDeselect ? -1 : params.dataIndex);
        },
        // Mutate the ref directly — no setState, no re-render. See hoveredFieldNameRef above.
        mouseover: (params: { componentType?: string; seriesName?: string }) => {
          if (params.componentType !== 'series') return;
          hoveredFieldNameRef.current = params.seriesName ?? null;
        },
        mouseout: (params: { componentType?: string }) => {
          if (params.componentType !== 'series') return;
          hoveredFieldNameRef.current = null;
        },
      }),
      [onBarSelect, effectiveSelectedIndex],
    );

    if (!option) return null;

    let finalOption: EChartsOption;
    if (typeof optionOverrides === 'function') {
      finalOption = optionOverrides(option as EChartsOption);
    } else if (optionOverrides) {
      finalOption = { ...option, ...optionOverrides } as EChartsOption;
    } else {
      finalOption = option as EChartsOption;
    }

    return (
      <>
        <EChartsBase
          ref={chartRef}
          option={finalOption as EChartsOption}
          onEvents={onEvents}
          onChartReady={handleChartReady}
          height={height}
          renderer={renderer}
          testId='echart-bar-chart'
        />
        {needsWindowing && !showZoomSlider && (
          <ChartWindowControls
            startLabel={data?.[windowStart]?.category ?? ''}
            endLabel={data?.[windowEnd]?.category ?? ''}
            hasPrev={windowStart > 0}
            hasNext={windowEnd < totalCount - 1}
            onPrev={onPrev}
            onNext={onNext}
          />
        )}
      </>
    );
  }),
);

export default BarChart;
